import { Injectable } from '@angular/core';
import { CheckoutBasket, RemoteData } from '@common/util-models';
import { filterNullUndefined } from '@domgen/dgx-fe-common';
import { Dictionary } from '@ngrx/entity';
import { Store, select } from '@ngrx/store';
import {
  BundleApplianceDetails,
  BundleApplianceDetailsBasket,
  RemoveBundleAppliancesAPIApplianceDetails,
  SkyBasketItemQuoteDetails,
  SkyMobilePriceConfig,
} from '@sky/util-models';
import { Observable } from 'rxjs';
import { BasketState } from './basket.reducer';
import * as DevicesActions from './devices/devices.actions';
import * as DevicesSelectors from './devices/devices.selectors';
import * as QuotesActions from './quotes/quotes.actions';
import * as QuotesSelectors from './quotes/quotes.selectors';

@Injectable()
export class BasketFacade {
  quotesLoaded$: Observable<RemoteData> = this.store.pipe(
    select(QuotesSelectors.getQuotesLoaded)
  );

  basketLoaded$: Observable<RemoteData> = this.store.pipe(
    select(DevicesSelectors.getDevicesLoaded)
  );

  numberOfIncludedDevices$: Observable<number> = this.store.pipe(
    select(QuotesSelectors.getNumberOfIncludedDevices),
    filterNullUndefined()
  );

  numberOfAdditionalDevices$: Observable<number> = this.store.pipe(
    select(QuotesSelectors.getNumberOfAdditionalDevices),
    filterNullUndefined()
  );

  totalNumberOfDevices$: Observable<number> = this.store.pipe(
    select(QuotesSelectors.getTotalNumberOfDevices),
    filterNullUndefined()
  );

  getPurchasePrice$: Observable<number> = this.store.pipe(
    select(QuotesSelectors.getPurchasePrice),
    filterNullUndefined()
  );

  selectedQuote$: Observable<SkyBasketItemQuoteDetails | undefined> =
    this.store.pipe(select(QuotesSelectors.getSelectedQuote));

  selectedQuotePrice$: Observable<number> = this.store.pipe(
    select(QuotesSelectors.getSelectedQuotePrice),
    filterNullUndefined()
  );

  skyBoxPrice$: Observable<number> = this.store.pipe(
    select(QuotesSelectors.getSkyBoxPrice),
    filterNullUndefined()
  );

  numberOfFreePayments$: Observable<number> = this.store.pipe(
    select(QuotesSelectors.getNumberOfFreePayments),
    filterNullUndefined()
  );

  extraDevicePrice$: Observable<number> = this.store.pipe(
    select(QuotesSelectors.getExtraDevicePrice),
    filterNullUndefined()
  );

  ltPrice$: Observable<number> = this.store.pipe(
    select(QuotesSelectors.getLtPrice),
    filterNullUndefined()
  );

  skyLinkedDevices$: Observable<BundleApplianceDetailsBasket[]> =
    this.store.pipe(select(DevicesSelectors.getSkyLinkedDevices));

  devicesEntities$: Observable<Dictionary<BundleApplianceDetailsBasket>> =
    this.store.pipe(select(DevicesSelectors.getDevicesEntities));

  devices$: Observable<BundleApplianceDetailsBasket[]> = this.store.pipe(
    select(DevicesSelectors.getAllDevices)
  );

  skyMobilePriceConfig$: Observable<SkyMobilePriceConfig[] | undefined> =
    this.store.pipe(select(DevicesSelectors.getSkyMobilePriceConfig));

  isEmpty$: Observable<boolean> = this.store.pipe(
    select(DevicesSelectors.isEmpty)
  );

  isSkyEssential$: Observable<boolean> = this.store.pipe(
    select(DevicesSelectors.isSkyEssential)
  );

  getAllImeiInBasket$: Observable<string[]> = this.store.pipe(
    select(DevicesSelectors.getAllImeiInBasket)
  );

  checkoutBasket$: Observable<CheckoutBasket> = this.store.pipe(
    select(QuotesSelectors.getCheckoutBasket),
    filterNullUndefined()
  );

  constructor(private store: Store<BasketState>) {}

  loadQuotes(applianceCode: string) {
    this.store.dispatch(QuotesActions.loadQuotes({ applianceCode }));
  }

  addDevicesToBasket(devices: BundleApplianceDetails[]) {
    this.store.dispatch(DevicesActions.addDevicesToBasket({ devices }));
  }

  addDevicesToBasketRedirect(devices: BundleApplianceDetails[]) {
    this.store.dispatch(DevicesActions.addDeviceToBasketRedirect({ devices }));
  }

  removeAllDevices() {
    this.store.dispatch(DevicesActions.removeAll());
  }

  removeDevicesFromBasket(
    deviceIds: RemoveBundleAppliancesAPIApplianceDetails[],
    redirectBack?: boolean
  ) {
    this.store.dispatch(
      DevicesActions.deleteDevicesFromBasket({ deviceIds, redirectBack })
    );
  }
}
